import {
  forkJoin as observableForkJoin,
  combineLatest as observableCombineLatest,
  of as observableOf,
  Observable,
  Subscription,
  ConnectableObservable,
} from 'rxjs';

import {
  share,
  publishReplay,
  first,
  switchMap,
  distinctUntilChanged,
  debounceTime,
  takeWhile,
  catchError,
  map,
  mergeMap,
} from 'rxjs/operators';
import {ChangeDetectorRef, NgZone, OnDestroy, Directive} from '@angular/core';
import {ResourceService} from '../../resource.service';
import {Resource} from '../../resource';
import {AbstractControlComponent, ControlTargetDontUpdateValue} from './abstract-control';
import {LayoutService} from '../../../layout/layout.service';
import {FilterResult} from '../../filterresult';
import {CacheService} from '../../../cache/cache.service';
import {SearchScopeConfiguration} from '../../../cache/uiconfiguration/searchscopeconfiguration';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {MatInput} from '@angular/material/input';
import {LanguageService} from '../../../language/language.service';
import {AddViewEvent} from '../../events/addviewevent';
import {ValidatorFn, Validators} from '@angular/forms';
import {ValuesObject} from '../detail-tabconfig';
import {allowedSelectionValidator} from './validators/allowedSelection-validator';
import {DisplayAttribute} from '../../displayattribute';
import {MultivalueDialogComponent} from './multivalue-dialog/multivalue-dialog.component';
import {MultivalueDialogResult} from './multivalue-dialog/multivalue-dialog-result';
import {DialogsService} from '../../../shared/dialog/dialogs.service';
import {ErrorService} from '../../../error/error.service';
import {ControlParentInterface} from '../control-parent.interface';
import {EnvironmentConfigService} from '../../../core/services/environment-config.service';

@Directive()
export abstract class CtrlIdentitypickerSingleAbstract
  extends AbstractControlComponent
  implements OnDestroy
{
  abstract objectDisplayName: string;

  constructor(
    parent: ControlParentInterface,
    private dialog: MatDialog,
    private dialogsService: DialogsService,
    private resourceService: ResourceService,
    private layoutService: LayoutService,
    private cacheService: CacheService,
    changeDetector: ChangeDetectorRef,
    _ngZone: NgZone,
    errorService: ErrorService,
    private envConfig: EnvironmentConfigService,
  ) {
    super(parent, 'Value', 'Value', changeDetector, _ngZone, errorService);
    this.cacheService.getLanguageResource().subscribe(lr => {
      this._allowedSelectionDefaultErrorMessage = LanguageService.translate(
        'CtrlIdentitypickerSingleAllowedSelectionDefaultErrorMessage',
        'You are not allowed to select the selected value.',
        lr,
      );
      this.emptyDisplayNameLabel = this.cacheService
        .getDisplayAttribute(
          this.envConfig.getAttributeName('DisplayName'),
          this.envConfig.getObjectTypeName('Resource'),
        )
        .pipe(
          map((da: DisplayAttribute) => {
            return (
              '⟨ ' + LanguageService.translate('DatatableNo', 'No', lr) + ' ' + da.Caption + ' ⟩'
            );
          }),
          catchError((error, caught) => this.handleSoftError(error, caught)),
        );
    });
  }

  guidObs: ConnectableObservable<Resource>[] = [];
  guidObsSubscriptions: Subscription[] = [];

  private attributesToSearchObs: Observable<string[]> = null;

  private _allowedSelectionErrorMessage: string = null;
  private _allowedSelectionDefaultErrorMessage: string = null;

  get allowedSelectionErrorMessage(): string {
    return this._allowedSelectionErrorMessage
      ? this._allowedSelectionErrorMessage
      : this._allowedSelectionDefaultErrorMessage;
  }

  emptyDisplayNameLabel: Observable<string> = observableOf('⟨ No Display Name ⟩');
  emptyResult: boolean = false;

  showBtOpen: boolean = true;

  // Getter for template access to environment attributes
  get myEnv() {
    return {
      Attributes: {
        ObjectID: this.envConfig.getAttributeName('ObjectID'),
        DisplayName: this.envConfig.getAttributeName('DisplayName'),
      },
    };
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.guidObsSubscriptions.forEach(s => s.unsubscribe());
  }

  filteredResources: Observable<Resource[]> = null;

  static PickedGuidRegex: RegExp = /^[0-9a-f]{8}\u2013([0-9a-f]{4}\u2013){3}[0-9a-f]{12}$/;

  abstract inputField: MatInput;

  protected initValidators(): void {
    if (this._config == null || this.formControl == null) {
      throw Error('Initialized target too early.');
    }

    observableCombineLatest(
      this._config.getBooleanPropertyValues('Required'),
      this._config.getProperty('AllowedSelection'),
      (required: boolean, allowedSelectionVo: ValuesObject<string[]>) => {
        const validators: ValidatorFn[] = [];
        if (required === true) {
          validators.push(Validators.required);
          validators.push(Validators.pattern(CtrlIdentitypickerSingleAbstract.PickedGuidRegex));
        }

        if (allowedSelectionVo != null) {
          let allowed: string[] = allowedSelectionVo.Values;
          if (allowed != null) {
            allowed = allowed.map(as => this.formatValue(as));
          }
          validators.push(allowedSelectionValidator(allowed));
        }

        this.formControl.setValidators(validators);
        this.formControl.updateValueAndValidity({emitEvent: false});
      },
    )
      .pipe(
        catchError((error, caught) => this.handleError(error, caught)),
        takeWhile(() => this.alive),
      )
      .subscribe();

    if (this._config.propertyExists('AllowedSelectionErrorMessage')) {
      this.getCaughtObservable(this._config.getPropertyValues('AllowedSelectionErrorMessage'))
        .pipe(takeWhile(() => this.alive))
        .subscribe((asErrMsg: string) => {
          if (asErrMsg == null || asErrMsg.trim().length === 0) {
            this._allowedSelectionErrorMessage = null;
          } else {
            this._allowedSelectionErrorMessage = asErrMsg.trim();
          }
        });
    }

    if (this._config.propertyExists('ShowButton')) {
      this._config
        .getBooleanPropertyValues('ShowButton')
        .pipe(
          takeWhile(() => this.alive),
          catchError((error, caught) => this.handleError(error, caught)),
        )
        .subscribe((val: boolean) => {
          this.showBtOpen = val;
        });
    }

    const valueChangeObs = this.formControl.valueChanges.pipe(
      catchError((error, caught) => this.handleError(error, caught)),
      takeWhile(() => this.alive),
      debounceTime(300),
      distinctUntilChanged(),
    );

    this.attributesToSearchObs = this._config.getPropertyValues('AttributesToSearch').pipe(
      takeWhile(() => this.alive),
      map((str: string) => {
        if (str == null || str.trim().length === 0) {
          return [
            this.envConfig.getAttributeName('DisplayName'),
            this.envConfig.getAttributeName('Description'),
            this.envConfig.getAttributeName('ObjectType'),
          ];
        } else {
          return str.split(',').map(attr => attr.trim());
        }
      }),
    );

    if (this._config.propertyExists('AutocompleteFilter')) {
      const filterObs = this._config
        .getPropertyValues('AutocompleteFilter')
        .pipe(takeWhile(() => this.alive));
      this.initFilter(filterObs, valueChangeObs);
    } else if (this._config.propertyExists('UsageKeywords')) {
      this.initUsageKeywords(valueChangeObs);
    } else if (this._config.propertyExists('Filter')) {
      const filterObs = this._config.getPropertyValues('Filter').pipe(takeWhile(() => this.alive));
      this.initFilter(filterObs, valueChangeObs);
    }
  }

  private initUsageKeywords(valueChangeObs: Observable<any>) {
    const usageKeywordObs: Observable<string[]> = this._config
      .getPropertyValues('UsageKeywords')
      .pipe(
        map((strVal: string) => strVal.split(',')),
        takeWhile(() => this.alive),
      );

    observableCombineLatest(
      this.attributesToSearchObs,
      usageKeywordObs,
      (attributesToSearch: string[], keywords: string[]) => {
        let sscsObs: Observable<SearchScopeConfiguration[]>[] = [];
        keywords.forEach((keyword: string) => {
          sscsObs.push(this.cacheService.getSearchScopeConfigurationForObjectType(keyword));
        });

        let activeRequests = 0;

        observableForkJoin(sscsObs).subscribe((sscss: SearchScopeConfiguration[][]) => {
          this.filteredResources = valueChangeObs.pipe(
            switchMap((searchTerm: string) => {
              this.layoutService.updatePreloaderState('active');
              this.emptyResult = false;
              this.changeDetector.markForCheck();

              let requests: Observable<Resource[]>[] = [];

              if (
                searchTerm == null ||
                searchTerm.trim().length === 0 ||
                CtrlIdentitypickerSingleAbstract.PickedGuidRegex.test(searchTerm)
              ) {
                this.layoutService.updatePreloaderState('hide-all');
                return observableOf([]);
              }

              sscss.forEach((sscs: SearchScopeConfiguration[]) => {
                sscs.forEach((ssc: SearchScopeConfiguration) => {
                  const reqAttributes =
                    ssc.attributesearched.indexOf(
                      this.envConfig.getAttributeName('DisplayName'),
                    ) === -1
                      ? ssc.attributesearched.join(',') +
                        ',' +
                        this.envConfig.getAttributeName('DisplayName')
                      : ssc.attributesearched.join(',');
                  const reqObs = this.resourceService
                    .searchAndFilterResources(
                      ssc.xpath,
                      searchTerm,
                      ssc.attributesearched,
                      reqAttributes,
                      100,
                    )
                    .pipe(
                      map(fr => {
                        if (fr === null || fr.Finished === true) {
                          activeRequests--;
                          if (activeRequests <= 0) {
                            this.layoutService.updatePreloaderState('hide-all');
                          }
                        }
                        if (fr == null || fr.Results == null) return [];
                        return fr.Results.map((res: Resource) => {
                          res['__tooltip_text'] = null;
                          if (ssc.attributesearched != null) {
                            const obsList: Observable<string>[] = [];
                            ssc.attributesearched.forEach((attr: string) => {
                              if (res[attr] == null) return;
                              const obs = this.cacheService
                                .getDisplayAttribute(
                                  attr,
                                  res[this.envConfig.getAttributeName('ObjectType')],
                                )
                                .pipe(map((da: DisplayAttribute) => da.Caption + ': ' + res[attr]));
                              obsList.push(obs);
                            });
                            res['__tooltip_text'] = observableCombineLatest(obsList).pipe(
                              map((vs: string[]) => vs.join('\n')),
                              first(),
                            );
                          }
                          return res;
                        });
                      }),
                    );
                  requests.push(reqObs);
                  activeRequests++;
                });
              });

              if (requests.length === 0) {
                this.layoutService.updatePreloaderState('hide-all');
                return observableOf([]);
              }

              return observableForkJoin(requests).pipe(
                map((rss: Resource[][]) => {
                  const result: Resource[] = [].concat.apply([], rss);
                  const data: any = Object.create(null);
                  const distinctResult: Resource[] = [];
                  result.forEach((r: Resource) => {
                    if (!(r[this.envConfig.getAttributeName('ObjectID')] in data)) {
                      data[r[this.envConfig.getAttributeName('ObjectID')]] = true;
                      distinctResult.push(r);
                    }
                  });

                  this.emptyResult = distinctResult.length === 0;
                  return distinctResult;
                }),
              );
            }),
            catchError((error, caught) => this.handleError(error, caught)),
          );
        });
      },
    )
      .pipe(takeWhile(() => this.alive))
      .subscribe();
  }

  private initFilter(filterObs: Observable<string>, valueChangeObs: Observable<any>) {
    observableCombineLatest(
      this.attributesToSearchObs,
      filterObs,
      (attributes: string[], filter: string) => {
        this.filteredResources = valueChangeObs.pipe(
          switchMap((searchTerm: string) => {
            this.layoutService.updatePreloaderState('active');
            this.emptyResult = false;
            this.changeDetector.markForCheck();

            if (
              searchTerm == null ||
              searchTerm.trim().length === 0 ||
              CtrlIdentitypickerSingleAbstract.PickedGuidRegex.test(searchTerm)
            ) {
              this.layoutService.updatePreloaderState('hide-all');
              return observableOf([]);
            }

            const reqAttributes =
              attributes.indexOf(this.envConfig.getAttributeName('DisplayName')) === -1
                ? attributes.join(',') + ',displayname'
                : attributes.join(',');

            return this.resourceService
              .searchAndFilterResources(filter, searchTerm, attributes, reqAttributes, 100)
              .pipe(
                map(fr => {
                  if (fr === null || fr.Finished === true) {
                    this.layoutService.updatePreloaderState('hide-all');
                  }
                  this.emptyResult = fr == null || fr.Results.length === 0;

                  if (fr == null || fr.Results == null) return [];
                  return fr.Results.map((res: Resource) => {
                    res['__tooltip_text'] = null;
                    if (attributes != null) {
                      const obsList: Observable<string>[] = [];
                      attributes.forEach((attr: string) => {
                        if (res[attr] == null) return;
                        const obs = this.cacheService
                          .getDisplayAttribute(
                            attr,
                            res[this.envConfig.getAttributeName('ObjectType')],
                          )
                          .pipe(map((da: DisplayAttribute) => da.Caption + ': ' + res[attr]));
                        obsList.push(obs);
                      });
                      res['__tooltip_text'] = observableCombineLatest(obsList).pipe(
                        map((vs: string[]) => vs.join('\n')),
                        first(),
                      );
                    }
                    return res;
                  });
                }),
              );
          }),
          catchError((error, caught) => this.handleError(error, caught)),
        );
      },
    )
      .pipe(takeWhile(() => this.alive))
      .subscribe();
  }

  protected formatSource(source: any): any {
    if (source == null) return null;
    if (!(typeof source === 'string')) return null;
    if (ResourceService.guidRegex.test(source) !== true) return null;
    return this.formatValue(source);
  }

  protected formatTarget(value: any): any {
    if (value == null) return null;
    if (CtrlIdentitypickerSingleAbstract.PickedGuidRegex.test(value)) {
      value = this.unformatValue(value);
      this.cacheService.addRecentGuid(value);
      return value;
    } else if (ResourceService.guidRegex.test(value)) {
      this.validateGuid(value);
      return new ControlTargetDontUpdateValue();
    } else {
      return new ControlTargetDontUpdateValue();
    }
  }

  displayName(guid: string): Observable<string> {
    if (guid == null || !CtrlIdentitypickerSingleAbstract.PickedGuidRegex.test(guid)) return null;
    guid = this.unformatValue(guid);
    let obs = this.guidObs[guid];
    if (obs == null) {
      obs = this.resourceService.getBasicResource(guid).pipe(
        catchError((error, caught) => this.handleError(error, caught)),
        map((res: Resource) => {
          if (res) {
            return res[this.envConfig.getAttributeName('DisplayName')];
          } else {
            // if result resource is empty, the user had no permission to view the resource
            return null;
          }
        }),
        publishReplay(1),
      );
      this.guidObsSubscriptions.push(obs.connect());
      this.guidObs[guid] = obs;
    }

    obs = obs.pipe(
      mergeMap((dn: string) => {
        if (dn == null || dn.trim().length === 0) {
          return this.emptyDisplayNameLabel;
        } else {
          return observableOf(dn);
        }
      }),
    );

    return obs;
  }

  private resourceClick(guid: string, event: MouseEvent) {
    guid = this.unformatValue(guid);
    if (event.ctrlKey || event.button === 1) {
      window.open('#/get/' + guid);
    } else {
      this._parent.onAddView.emit(new AddViewEvent('get', guid));
    }
  }

  showAutocomplete(): Observable<boolean> {
    return this.getCaughtObservable(
      this.isEnabled.pipe(
        map(
          (enabled: boolean) =>
            enabled &&
            (this.formControl.value == null ||
              !CtrlIdentitypickerSingleAbstract.PickedGuidRegex.test(this.formControl.value)),
        ),
      ),
    ).pipe(distinctUntilChanged(), share());
  }

  onRemoveClick(): void {
    this.formControl.setValue(null);
    this.inputField.focus();
    setTimeout(() => this.inputField.focus(), 100);
    setTimeout(() => this.inputField.focus(), 500);
    setTimeout(() => this.inputField.focus(), 1000);
  }

  formatValue(optionValue: string): string {
    return optionValue.replace(/-/g, '\u2013');
  }

  unformatValue(formattedOptionValue: string): string {
    return formattedOptionValue.replace(/\u2013/g, '-');
  }

  validateGuid(guid: string): void {
    if (this._config.propertyExists('Filter')) {
      this.layoutService.updatePreloaderState('active');
      this._config
        .getPropertyValues('Filter')
        .pipe(first())
        .subscribe((filter: string) => {
          if (filter != null) {
            const regex = /^([^[]+)\[(.+)\]$/;
            if (regex.test(filter)) {
              let matches: RegExpExecArray = regex.exec(filter);
              filter =
                matches[1] +
                '[(' +
                matches[2] +
                ') and (' +
                this.envConfig.getAttributeName('ObjectID') +
                "='" +
                guid +
                "')]";
            } else {
              filter =
                filter + '[' + this.envConfig.getAttributeName('ObjectID') + "='" + guid + "']";
            }
            this.resourceService
              .searchResources(filter, this.envConfig.getAttributeName('ObjectID'), 1)
              .pipe(
                map((fr: FilterResult) => (fr ? fr.TotalCount === 1 : false)),
                first(),
              )
              .subscribe((valid: boolean) => {
                this.layoutService.updatePreloaderState('hide');
                if (valid === true) {
                  this.formControl.setValue(this.formatValue(guid));
                } else {
                  this.formControl.setValue(null);
                }
              });
          } else {
            this.formControl.setValue(null);
            this.layoutService.updatePreloaderState('hide');
          }
        });
    } else if (this._config.propertyExists('UsageKeywords')) {
      this.layoutService.updatePreloaderState('active');
      this._config
        .getPropertyValues('UsageKeywords')
        .pipe(
          map((strVal: string) => strVal.split(',')),
          first(),
        )
        .subscribe((keywords: string[]) => {
          let sscsObs: Observable<SearchScopeConfiguration[]>[] = [];
          keywords.forEach((keyword: string) => {
            sscsObs.push(this.cacheService.getSearchScopeConfigurationForObjectType(keyword));
          });

          observableForkJoin(sscsObs).subscribe((sscss: SearchScopeConfiguration[][]) => {
            let searches: Observable<boolean>[] = [];
            sscss.forEach((sscs: SearchScopeConfiguration[]) => {
              sscs.forEach((ssc: SearchScopeConfiguration) => {
                let filter: string = ssc.xpath;
                if (filter != null) {
                  const regex = /^([^[]+)\[(.+)\]$/;
                  if (regex.test(filter)) {
                    let matches: RegExpExecArray = regex.exec(filter);
                    filter =
                      matches[1] +
                      '[(' +
                      matches[2] +
                      ') and (' +
                      this.envConfig.getAttributeName('ObjectID') +
                      "='" +
                      guid +
                      "')]";
                  } else {
                    filter =
                      filter +
                      '[' +
                      this.envConfig.getAttributeName('ObjectID') +
                      "='" +
                      guid +
                      "']";
                  }
                  const searchObs = this.resourceService
                    .searchResources(filter, this.envConfig.getAttributeName('ObjectID'), 1)
                    .pipe(map((fr: FilterResult) => (fr ? fr.TotalCount === 1 : false)));
                  searches.push(searchObs);
                } else {
                  this.formControl.setValue(null);
                  this.layoutService.updatePreloaderState('hide');
                }
              });
            });

            if (searches.length === 0) {
              this.layoutService.updatePreloaderState('hide');
              this.formControl.setValue(null);
              return;
            }

            observableForkJoin(searches).subscribe((valids: boolean[]) => {
              let valid: boolean = false;

              for (let i = 0; i < valids.length; i++) {
                if (valids[i] === true) {
                  valid = true;
                  break;
                }
              }

              this.layoutService.updatePreloaderState('hide');
              if (valid === true) {
                this.formControl.setValue(this.formatValue(guid));
              } else {
                this.formControl.setValue(null);
              }
            });
          });
        });
    }
  }

  get autocompletePlaceholder(): Observable<string> {
    return this.getCaughtObservable(
      observableCombineLatest(
        this.cacheService.getLanguageResource(),
        this._config.Caption.pipe(map(valObj => (valObj ? valObj.Values : null))),
        (lr, label: string) => {
          return LanguageService.translate(
            'CtrlIdentitypickerSingleAutocompletePlaceholder',
            'Search for a {0}',
            lr,
            [label],
          );
        },
      ),
    );
  }

  onOpenClick() {
    if (this._config.propertyExists('UsageKeywords')) {
      this.manageWithUsageKeywords();
    } else if (this._config.propertyExists('Filter')) {
      this.manageWithFilter();
    }
  }

  private manageWithUsageKeywords(): void {
    this._config
      .getPropertyValues('UsageKeywords')
      .pipe(
        map((strVal: string) => strVal.split(',')),
        first(),
        catchError((error, caught) => this.handleError(error, caught)),
      )
      .subscribe((keywords: string[]) => {
        if (keywords == null || keywords.length === 0) {
          this.openManageDialog(observableOf(null));
          return;
        }
        const sscsObs: Observable<SearchScopeConfiguration[]>[] = [];
        keywords.forEach((keyword: string) => {
          sscsObs.push(this.cacheService.getSearchScopeConfigurationForObjectType(keyword));
        });

        const sscsObsJoined = observableForkJoin(sscsObs).pipe(
          map((sscss: SearchScopeConfiguration[][]) => {
            return [].concat.apply([], sscss);
          }),
        );

        this.openManageDialog(sscsObsJoined);
      });
  }

  private manageWithFilter(): void {
    this._config
      .getPropertyValues('Filter')
      .pipe(
        first(),
        catchError((error, caught) => this.handleError(error, caught)),
      )
      .subscribe((filter: string) => {
        if (filter == null || filter.length === 0) {
          this.openManageDialog(observableOf(null));
          return;
        }
        const ssc: SearchScopeConfiguration = <SearchScopeConfiguration>{
          id: null,
          displayname: null,
          xpath: filter,
          resultobjecttype: null,
          attributedisplayed: null,
        };

        if (this._config.propertyExists('MainSearchScreenText')) {
          this._config
            .getPropertyValues('MainSearchScreenText')
            .pipe(
              first(),
              catchError((error, caught) => this.handleSoftError(error, caught)),
            )
            .subscribe((searchText: string) => {
              ssc.displayname = searchText;
              this.openManageDialog(observableOf([ssc]));
            });
        } else {
          this.openManageDialog(observableOf([ssc]));
        }
      });
  }

  private openManageDialog(searchScopesObs: Observable<SearchScopeConfiguration[]>): void {
    const resObjTypeObs = this._config.getPropertyValues('ResultObjectType');
    const columnsObs = this._config.getPropertyValues('ColumnsToDisplay');
    const languageResObs = this.cacheService.getLanguageResource();

    observableCombineLatest(
      searchScopesObs,
      this.sourceVoObs,
      resObjTypeObs,
      columnsObs,
      this.attributesToSearchObs,
      languageResObs,
      (
        sscs: SearchScopeConfiguration[],
        source: ValuesObject<any>,
        resObjType: string,
        columnsStr: string,
        attributesToSearch: string[],
        lr: any,
      ) => {
        let columns: string[];
        if (columnsStr != null && columnsStr !== '') {
          columns = columnsStr.split(',').map(x => x.trim());
        } else {
          columns = [this.envConfig.getAttributeName('DisplayName')];
        }

        if (resObjType == null) {
          resObjType = this.envConfig.getObjectTypeName('Resource');
        }

        if (sscs != null && sscs.length === 1) {
          if (sscs[0].displayname == null) {
            sscs[0].displayname = LanguageService.translate('Search', 'Search', lr);
          }
          if (sscs[0].resultobjecttype == null) {
            sscs[0].resultobjecttype = resObjType;
          }
          if (sscs[0].attributedisplayed == null) {
            sscs[0].attributedisplayed = columns;
          }

          if (sscs[0].attributesearched == null) {
            sscs[0].attributesearched = attributesToSearch;
          }
        }

        if (sscs == null || sscs.length === 0) {
          throw {
            code: 'SearchScopesEmpty',
            message: 'No search scopes defined. Can not open dialog.',
          };
        }

        let dialogRef: MatDialogRef<MultivalueDialogComponent> =
          this.dialog.open(MultivalueDialogComponent);
        if (typeof this.formControl.value === 'string') {
          dialogRef.componentInstance.initFilterValue = this.formControl.value;
        }

        if (this._config.propertyExists('ListViewAddTitle')) {
          this._config
            .getPropertyValues('ListViewAddTitle')
            .pipe(
              first(),
              catchError((error, caught) => this.handleSoftError(error, caught)),
            )
            .subscribe((title: string) => {
              dialogRef.componentInstance.addListTitle = title;
            });
        }
        dialogRef.componentInstance.objectDisplayName = this.objectDisplayName;
        dialogRef.componentInstance.attributeLabel = this.getCaughtObservable(
          this._config.Caption.pipe(map(vo => (vo ? vo.Values : null))),
        );
        dialogRef.componentInstance.SearchScopes = sscs;
        dialogRef.componentInstance.selectionOnly = true;
        dialogRef.componentInstance.singleSelection = true;
        dialogRef
          .afterClosed()
          .pipe(
            first(),
            catchError((error, caught) => this.handleSoftError(error, caught)),
          )
          .subscribe((result: MultivalueDialogResult) => {
            if (
              result != null &&
              result.CurrentlySelected != null &&
              result.CurrentlySelected.length > 0
            ) {
              this.formControl.setValue(this.formatValue(result.CurrentlySelected[0]));
              this.formControl.markAsTouched();
              this.changeDetector.markForCheck();
            }
          });
      },
    )
      .pipe(
        first(),
        catchError((error, caught) => this.handleSoftError(error, caught)),
      )
      .subscribe();
  }

  handleSoftError(error: any, caught?: Observable<any>) {
    this.cacheService.getLanguageResource().subscribe(lr => {
      const title: string = LanguageService.translate(
        'CtrlIdentitypickerSingleErrorTitle',
        'Error in Identity Picker',
        lr,
      );
      let errorMessage: string;
      if (error.code === 'SearchScopesEmpty') {
        errorMessage = LanguageService.translate(
          'CtrlIdentitypickerSingleNoSearchScopesError',
          'No search scopes defined. Can not open dialog.',
          lr,
        );
      } else if (typeof error.message === 'string') {
        errorMessage = error.message;
      } else {
        errorMessage = error;
      }
      this.dialogsService.error(title, errorMessage, null);
    });

    console.error(error);
    return observableOf(null);
  }
}
